import React from 'react';
import { motion } from 'framer-motion';

function Projects() {
  const projects = [
    {
      id: 1,
      title: "AI Product Management Agent",
      description: "An intelligent agent that helps product managers streamline their workflow and make data-driven decisions. Built with advanced AI capabilities to automate routine tasks and provide strategic insights.",
      status: "In Development",
      technologies: ["AI", "Machine Learning", "Product Management"],
      image: "/images/ai-robot.svg",
      link: "#"
    },
    {
      id: 2,
      title: "Multitexter.com",
      description: "Led product development for a bulk SMS platform, implementing user-centric features and analytics. Managed the product roadmap, user feedback, and feature prioritization to drive platform growth.",
      status: "Completed",
      technologies: ["SMS", "API", "Product Management", "Analytics"],
      image: "/images/screenshots/multitexter.jpg",
      link: "https://multitexter.com"
    },
    {
      id: 3,
      title: "OgaRecharge.com",
      description: "Spearheaded product strategy for a digital recharge platform, focusing on user experience and payment processing. Managed cross-functional teams to deliver seamless telecom integration.",
      status: "Completed",
      technologies: ["Payment Processing", "Telecom Integration", "Product Strategy"],
      image: "/images/screenshots/ogarecharge.jpg",
      link: "https://ogarecharge.com"
    },
    {
      id: 4,
      title: "SMSVend.com",
      description: "Directed product development for a B2B SMS vending platform. Implemented features for bulk purchasing, pricing tiers, and reseller management while ensuring scalability.",
      status: "Completed",
      technologies: ["B2B", "SMS", "Product Management", "User Management"],
      image: "/images/screenshots/smsvend.jpg",
      link: "https://smsvend.com"
    },
    {
      id: 5,
      title: "Fullware.com",
      description: "Led product strategy for a comprehensive software marketplace. Managed the entire product lifecycle, from ideation to launch, focusing on enterprise-grade software solutions.",
      status: "Completed",
      technologies: ["Marketplace", "Software Licensing", "Product Strategy"],
      image: "/images/screenshots/fullware.jpg",
      link: "https://fullware.group"
    },
    {
      id: 6,
      title: "Capital One - Customer Data & API Infrastructure",
      description: "Managed product development for customer data and API infrastructure. Led cross-functional teams in implementing scalable solutions for data processing and API management.",
      status: "Completed",
      technologies: ["Data Infrastructure", "API Development", "Product Management"],
      image: "/images/screenshots/capital-one.jpg",
      link: "https://capitalone.com"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black py-12 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
            Projects & Experience
          </h1>
          <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-300 sm:mt-4">
            A showcase of my work in product management and technology
          </p>
        </motion.div>

        <div className="mt-12 space-y-12">
          {projects.map((project, index) => (
            <motion.div 
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="backdrop-blur-lg bg-gray-900/50 rounded-2xl border border-gray-700 shadow-xl overflow-hidden hover:border-indigo-500/50 transition-all duration-300"
            >
              <div className="md:flex">
                <div className="md:flex-shrink-0 relative group">
                  <img
                    className="h-48 w-full md:w-48 object-cover transition-transform duration-300 group-hover:scale-105"
                    src={project.image}
                    alt={project.title}
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-white">{project.title}</h2>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      project.status === "In Development" 
                        ? "bg-yellow-500/20 text-yellow-400" 
                        : "bg-green-500/20 text-green-400"
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  <p className="mt-4 text-gray-300">{project.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {project.technologies.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-gray-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6">
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-indigo-400 hover:text-indigo-300"
                    >
                      Learn more
                      <svg
                        className="ml-2 w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href="https://linkedin.com/in/ikunle"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            View Full LinkedIn Profile
          </a>
        </div>
      </div>
    </div>
  );
}

export default Projects; 